import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePendingSurveys } from "@/hooks/usePendingSurveys";

const ORANGE = "#C94F13";

const seenKey = (id: string) => `survey_modal_seen_${id}`;

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function SurveyPendingBanner() {
  const navigate = useNavigate();
  const { pendingAssignments, isLoading } = usePendingSurveys();

  const visible = useMemo(() => {
    if (isLoading) return [];
    const now = Date.now();
    return pendingAssignments.filter((a) => {
      const notExpired = !a.dueAt || new Date(a.dueAt).getTime() > now;
      const seen = safeGet(seenKey(a.assignmentId)) === "true";
      return notExpired && seen;
    });
  }, [pendingAssignments, isLoading]);

  if (visible.length === 0) return null;

  const single = visible.length === 1 ? visible[0] : null;

  return (
    <div
      className="sticky top-14 sm:top-16 z-20 w-full"
      style={{
        backgroundColor: ORANGE,
        color: "#ffffff",
        fontFamily: "'Public Sans', Arial, sans-serif",
      }}
    >
      <div className="px-3 sm:px-4 lg:px-6 py-2 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <ClipboardList className="h-4 w-4 flex-shrink-0" />
          <span className="text-sm font-medium truncate">
            {single
              ? `You have a survey waiting: ${single.title}`
              : `You have ${visible.length} surveys waiting`}
          </span>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="bg-transparent text-white border-white hover:bg-white hover:text-foreground"
          onClick={() =>
            single ? navigate(`/survey/${single.assignmentId}`) : navigate("/dashboard")
          }
        >
          {single ? "Take Survey" : "View Surveys"}
        </Button>
      </div>
    </div>
  );
}

export default SurveyPendingBanner;
